import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, XCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CONSTRUCTION_ADDONS_ROI,
  type ConstructionAddonRoiPack,
} from "@/lib/content";

/**
 * `/pricing/construction/add-ons`
 *
 * Sells the three construction workflow packs (Subcontractor + Compliance,
 * Change Order + Claims, Lien Waiver + Pay App) on ROI rather than features.
 * Mirrors the conversion-optimized framing Procore / Autodesk / Levelset use
 * to justify add-on pricing to GCs, subs, and owners.
 *
 * Pricing (the actual list price for each pack) is intentionally left off
 * this page — the dollar copy here is recovered savings, not list price.
 * Wiring real Stripe SKUs lands with the pack feature work (#591 and
 * #593–#604) when those ship; until then this page is the surface sales
 * uses to justify the add-ons before quoting them.
 *
 * Visual conventions match `pricing-vertical.tsx` (Layout + SEOHead, the
 * same container widths and motion intros, the same gradient final CTA)
 * so the add-ons page feels native to the rest of the pricing surfaces.
 */

function PackSection({
  pack,
  index,
}: {
  pack: ConstructionAddonRoiPack;
  index: number;
}) {
  // Alternating page background so three back-to-back pack sections don't
  // run together — odd packs sit on the muted slate band, even packs on
  // the page background. Mirrors how `pricing-vertical.tsx` separates its
  // sections.
  const tinted = index % 2 === 1;
  return (
    <section
      id={`pack-${pack.id}`}
      data-testid={`pack-${pack.id}`}
      className={`py-20 md:py-24 scroll-mt-20 ${
        tinted
          ? "bg-slate-50 dark:bg-navy/30 border-y border-border/50"
          : ""
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <div className="text-center mb-10">
            <h2
              className="text-h2 mb-3 text-navy dark:text-white"
              data-testid={`pack-title-${pack.id}`}
            >
              {pack.title}
            </h2>
            <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              {pack.tagline}
            </p>
          </div>

          {/* Without / With ClauseArk comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div
              className="rounded-2xl border border-border bg-card p-6 md:p-8"
              data-testid={`pack-without-${pack.id}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <XCircle className="h-5 w-5 text-red-500" />
                <h3 className="text-base font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                  Without ClauseArk
                </h3>
              </div>
              <ul className="space-y-3">
                {pack.withoutClauseArk.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 rounded-full bg-red-400 shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-2xl border-2 border-indigo-200 dark:border-indigo-900/60 bg-card p-6 md:p-8"
              data-testid={`pack-with-${pack.id}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-indigo-500" />
                <h3 className="text-base font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                  With ClauseArk
                </h3>
              </div>
              <ul className="space-y-3">
                {pack.withClauseArk.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200"
                  >
                    <CheckCircle2 className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Typical Savings — tinted callout */}
          <div
            className="rounded-2xl border border-indigo-200 dark:border-indigo-900/60 bg-indigo-50/70 dark:bg-indigo-950/30 p-6 md:p-8 mb-6"
            data-testid={`pack-savings-${pack.id}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                Typical Savings
              </h3>
            </div>
            <ul className="space-y-2">
              {pack.typicalSavings.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm md:text-base font-medium text-navy dark:text-white"
                >
                  <span
                    aria-hidden="true"
                    className="mt-2 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Bottom Line — distinct emphasis block */}
          <div
            className="rounded-2xl bg-brand-gradient text-white p-6 md:p-7 text-center"
            data-testid={`pack-bottom-line-${pack.id}`}
          >
            <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-80 mb-2">
              Bottom Line
            </div>
            <p className="text-base md:text-lg font-semibold leading-snug">
              {pack.bottomLine}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function PricingConstructionAddonsPage() {
  const content = CONSTRUCTION_ADDONS_ROI;

  return (
    <Layout>
      <SEOHead title={content.seo.title} description={content.seo.description} />

      {/* ── Hero ── */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 text-center">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {content.hero.eyebrow}
            </div>
            <h1
              className="text-display mb-6 text-navy dark:text-white"
              data-testid="construction-addons-hero-title"
            >
              {content.hero.headline}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {content.hero.subhead}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Three pack sections ── */}
      {content.packs.map((pack, i) => (
        <PackSection key={pack.id} pack={pack} index={i} />
      ))}

      {/* ── Bundle ROI ── */}
      <section className="py-24" data-testid="construction-addons-bundle">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-10">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-3 tracking-wider uppercase">
                {content.bundle.eyebrow}
              </div>
              <h2
                className="text-h2 mb-4 text-navy dark:text-white"
                data-testid="construction-addons-bundle-title"
              >
                {content.bundle.heading}
              </h2>
              <p className="text-body-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                {content.bundle.body}
              </p>
            </div>

            <div className="rounded-3xl border border-border bg-card p-8 md:p-10 mb-10">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {content.bundle.impacts.map((impact, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm md:text-base"
                    data-testid={`construction-addons-bundle-impact-${i}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                    <span className="font-medium text-navy dark:text-white">
                      {impact}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-brand-gradient p-10 md:p-14 text-white text-center">
              <h3 className="text-h3 mb-3">
                Ready to put the packs to work on your projects?
              </h3>
              <p className="text-body-lg opacity-90 mb-8 max-w-xl mx-auto">
                Start with a Construction plan, or talk to sales if you want a
                guided rollout across multiple projects.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link href={content.bundle.primaryCta.path}>
                  <Button
                    size="lg"
                    className="bg-white text-indigo-700 hover:bg-white/90 h-11 px-6"
                    data-testid="construction-addons-cta-primary"
                  >
                    {content.bundle.primaryCta.label}
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
                <Link href={content.bundle.secondaryCta.path}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/40 text-white hover:bg-white/10 h-11 px-6"
                    data-testid="construction-addons-cta-secondary"
                  >
                    {content.bundle.secondaryCta.label}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
